/**
 * Modal para añadir códigos de barras a productos
 * Versión Material-UI del legacy ModalEan
 */
import React, { useState, useContext } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Box,
    Typography,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import QrCodeIcon from "@mui/icons-material/QrCode";
import NumbersIcon from "@mui/icons-material/Numbers";
import { StateContext } from "../../../../components/context/StateProvider";
import ProductService from "../../../../services/product.service";
import Toasts from "../../../../components/notificacion/Toasts";
import { FieldLabel } from "../ui";
import { BADGE_COLORS } from "../../../../components/ui/badgeColors";

const ModalAnadirEan = ({ productoId, updated = true, trigger }) => {
    const { state, updateState } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [open, setOpen] = useState(false);
    const [ean, setEan] = useState("");
    const [units, setUnits] = useState(1);
    const [loading, setLoading] = useState(false);

    // Buscar el producto por ID en el array de productos
    const productoIndex = state.products.findIndex(p => p.id === productoId);
    const producto = productoIndex !== -1 ? state.products[productoIndex] : null;

    const handleOpen = () => {
        setOpen(true);
        setEan("");
        setUnits(1);
    };

    const handleClose = () => {
        setOpen(false);
        setEan("");
        setUnits(1);
    };

    const generateEan = (ref, barras, unidades) => ({
        ARTICULO: ref,
        BARRAS: barras,
        UNIDADES: parseInt(unidades)
    });

    const handleAddEan = async () => {
        if (!producto) {
            const toasts = Toasts.generateToast("Producto no encontrado", "error");
            updateState({ toasts: [...state.toasts, toasts] });
            return;
        }

        if (ean === "") {
            const toasts = Toasts.generateToast("El código de barras es obligatorio", "warning");
            updateState({ toasts: [...state.toasts, toasts] });
            return;
        }

        setLoading(true);

        try {
            await ProductService.checkEanProduct(ean);

            const newEan = generateEan(producto.ref, ean, units);

            // Actualizar estado local usando el índice
            // Asegurar que updated existe
            if (!state.products[productoIndex].updated) {
                state.products[productoIndex].updated = {};
            }

            if (state.products[productoIndex].updated.barras) {
                state.products[productoIndex].updated.barras.push(newEan);
            } else {
                state.products[productoIndex].updated.barras = [newEan];
            }

            state.products[productoIndex].ean.push(newEan);

            if (updated) {
                // Guardar en backend
                const result = await ProductService.updateProduct(
                    state.products[productoIndex].ref,
                    state.products[productoIndex].updated,
                    false
                );

                const toasts = Toasts.generateToast(result.message, "success");
                state.products[productoIndex].updated = {};
                updateState({ products: state.products, toasts: [...state.toasts, toasts] });
            } else {
                updateState({ products: state.products });
            }

            handleClose();
        } catch (error) {
            const toasts = Toasts.generateToast(
                error.response?.data?.message || "Error al añadir código de barras",
                "error"
            );
            updateState({ toasts: [...state.toasts, toasts] });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Trigger button */}
            {trigger ? (
                <Box onClick={handleOpen}>{trigger}</Box>
            ) : (
                <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{
                        color: "#000",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.08)"
                        }
                    }}
                >
                    <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
                </IconButton>
            )}

            {/* Modal */}
            <Dialog
                open={open}
                onClose={loading ? undefined : handleClose}
                maxWidth="xs"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 2,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: { xs: 1, sm: 2 },
                        pb: { xs: 1.5, sm: 2 },
                        pt: { xs: 2, sm: 2.5 },
                        px: { xs: 2, sm: 3 }
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                        <Box
                            sx={{
                                width: { xs: 36, sm: 40 },
                                height: { xs: 36, sm: 40 },
                                borderRadius: "50%",
                                backgroundColor: BADGE_COLORS.AMBER.bg,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0
                            }}
                        >
                            <QrCodeIcon sx={{ color: BADGE_COLORS.AMBER.text, fontSize: { xs: 20, sm: 24 } }} />
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: "#212529",
                                fontSize: { xs: "1.1rem", sm: "1.25rem" }
                            }}
                        >
                            {isMobile ? "Añadir código" : "Añadir código de barras"}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        size="small"
                        sx={{
                            color: "#6c757d",
                            "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)"
                            }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent
                    sx={{
                        pt: { xs: 2, sm: 3 },
                        pb: { xs: 2, sm: 2 },
                        px: { xs: 2, sm: 3 },
                        flex: isMobile ? 1 : "initial"
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5 } }}>
                        {/* Información del producto */}
                        {producto && (
                            <Box
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: 2,
                                    border: "1px solid #e9ecef"
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: { xs: 11, sm: 12 },
                                        fontWeight: 600,
                                        color: "#6c757d",
                                        mb: 0.5
                                    }}
                                >
                                    Producto:
                                </Typography>
                                <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: "#212529" }}>
                                    {producto.name}
                                </Typography>
                            </Box>
                        )}

                        {/* Código de barras */}
                        <Box>
                            <FieldLabel icon={QrCodeIcon}>Código de barras *</FieldLabel>
                            <TextField
                                fullWidth
                                placeholder="Código EAN..."
                                value={ean}
                                onChange={(e) => setEan(e.target.value)}
                                disabled={loading}
                                autoFocus
                            />
                        </Box>

                        {/* Unidades */}
                        <Box>
                            <FieldLabel icon={NumbersIcon}>Unidades</FieldLabel>
                            <TextField
                                fullWidth
                                type="number"
                                placeholder="Unidades..."
                                value={units}
                                onChange={(e) => setUnits(e.target.value)}
                                disabled={loading}
                                inputProps={{ min: 1 }}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <Divider />

                <DialogActions
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        gap: { xs: 1, sm: 1.5 },
                        flexDirection: { xs: "column-reverse", sm: "row" }
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        fullWidth={isMobile}
                        disabled={loading}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            px: { xs: 2, sm: 2.5 },
                            py: { xs: 1.25, sm: 1 },
                            fontSize: { xs: 14, sm: 15 },
                            color: "#6c757d",
                            borderColor: "#6c757d",
                            "&:hover": {
                                borderColor: "#495057",
                                backgroundColor: "rgba(0,0,0,0.04)"
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddEan}
                        fullWidth={isMobile}
                        disabled={loading || !ean}
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : (
                                <AddCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            )
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            px: { xs: 2, sm: 2.5 },
                            py: { xs: 1.25, sm: 1 },
                            fontSize: { xs: 14, sm: 15 },
                            backgroundColor: "#cc071e",
                            "&:hover": {
                                backgroundColor: "#a30618"
                            },
                            "&:disabled": {
                                backgroundColor: "#6c757d",
                                color: "#fff"
                            }
                        }}
                    >
                        {loading ? "Añadiendo..." : isMobile ? "Añadir" : "Añadir código"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ModalAnadirEan;
